// Star marks for a rating out of 5: a row filled to the value (4.4 fills four and two fifths),
// and the compact rating the workflow lists show, stars with one decimal and the count, or
// "No ratings yet".
import { Star } from "@phosphor-icons/react";

import { useT } from "../i18n";
import { cn } from "../ui/cn";
import { formatRating, SCORES, type WorkflowStats } from "./model";

/** Decorative: the caller gives the value in words. */
export function Stars({ value, className }: { value: number; className?: string }) {
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-px text-orange", className)} aria-hidden>
      {SCORES.map((s) => {
        const fill = Math.max(0, Math.min(1, value - (s - 1)));
        return (
          <span key={s} className="relative inline-flex">
            <Star weight="regular" className="h-[1em] w-[1em] text-gray-300" />
            {fill > 0 ? (
              <span className="absolute inset-y-0 start-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
                <Star weight="fill" className="h-[1em] w-[1em]" />
              </span>
            ) : null}
          </span>
        );
      })}
    </span>
  );
}

/** Stars, the rating to one decimal and how many rated, or "No ratings yet". */
export function RatingSummary({ stats, className }: { stats: WorkflowStats; className?: string }) {
  const t = useT().ratings;
  if (stats.rating == null) return <span className={cn("whitespace-nowrap text-gray-500", className)}>{t.noRatings}</span>;
  const rating = formatRating(stats.rating);
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap", className)} title={t.outOf5(rating)}>
      <Stars value={stats.rating} className="text-[15px]" />
      <span className="tabular font-semibold text-gray-900">
        <span className="sr-only">{t.outOf5(rating)}</span>
        <span aria-hidden>{rating}</span>
      </span>
      <span className="tabular text-xs text-gray-500">({t.ratingCount(stats.rating_count)})</span>
    </span>
  );
}
